import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import type { AmDelta, DraftMove } from '../../lib/accountAnalytics';

// A compact per-AM delta preview ("Alice +5", "Bob −3", "Unassigned −2"). Shared
// by the draft banner + the confirm modal.
export function DeltaChips({ deltas }: { deltas: AmDelta[] }) {
  if (deltas.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {deltas.map((d) => (
        <span
          key={d.amKey}
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
            d.delta > 0
              ? 'bg-green-50 text-green-700 ring-green-600/20'
              : 'bg-red-50 text-red-700 ring-red-600/20',
          )}
          title={`${d.amLabel}: ${d.before} → ${d.after}`}
        >
          {d.amLabel} {d.delta > 0 ? `+${d.delta}` : d.delta}
        </span>
      ))}
    </div>
  );
}

// The "Draft mode" banner shown whenever the draft has effective moves. Surfaces
// the headline count, the per-AM delta preview, any commit errors, and the
// Revert / Confirm actions. Confirm opens the final-warning modal (owned by the
// page); nothing is written from here.
export function DraftPanel({
  moves,
  deltas,
  managerCount,
  errors,
  onRevert,
  onConfirm,
}: {
  moves: DraftMove[];
  deltas: AmDelta[];
  managerCount: number;
  errors: Array<{ accountId: string; reason: string }>;
  onRevert: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" role="region" aria-label="Draft mode">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-2">
          <p className="text-sm font-semibold text-amber-900">
            Draft mode — {moves.length} account{moves.length === 1 ? '' : 's'} moving across{' '}
            {managerCount} manager{managerCount === 1 ? '' : 's'}. Charts + stats below preview this
            draft (not live).
          </p>
          <DeltaChips deltas={deltas} />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onRevert}>
            Revert
          </Button>
          <Button size="sm" onClick={onConfirm}>
            Confirm…
          </Button>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700">
          <p className="font-medium">{errors.length} account{errors.length === 1 ? '' : 's'} failed to reassign — still in the draft:</p>
          <ul className="mt-1 list-inside list-disc">
            {errors.slice(0, 8).map((e) => (
              <li key={e.accountId}>
                {e.accountId}: {e.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
